if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = this.getSubmitButtons();
        this.submitButtonTexts = Array.from(this.submitButtons).map(button => button.querySelector('span'));

        // Also get the notify me button if it exists
        this.notifyMeButton = this.querySelector('#NotifyMeButton-' + this.dataset.sectionId);

        // Add click event listener to notify me button
        if (this.notifyMeButton) {
          this.notifyMeButton.addEventListener('click', this.openNotifyMeModal.bind(this));
        }

        if (document.querySelector('cart-drawer')) {
          this.submitButtons.forEach(button => button.setAttribute('aria-haspopup', 'dialog'));
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.safetyTimeout = null;

        this.setupSizeSelectionErrorHandler();
      }

      disconnectedCallback() {
        // Clean up any pending timeouts
        if (this.safetyTimeout) {
          clearTimeout(this.safetyTimeout);
          this.safetyTimeout = null;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.submitButtons = this.getSubmitButtons();
        this.submitButtonTexts = Array.from(this.submitButtons).map(button => button.querySelector('span'));

        if (evt.submitter && evt.submitter.getAttribute('aria-disabled') === 'true') return;

        // Block submit while a variant change is still being applied. The hidden
        // input[name="id"] is only written after the section fetch resolves, so
        // submitting now would post the previous (often SSR-default) variant.
        const productInfo = this.closest('product-info');
        if (productInfo?.dataset.variantPending === 'true') return;

        const variantSelects = document.querySelector('variant-selects');

        if (variantSelects) {
          const sizeFieldsets = Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
          let sizeInput = null;

          for (const fieldset of sizeFieldsets) {
            const legend = fieldset.querySelector('legend, label');
            const sizeInputByName = fieldset.querySelector('input[name*="Size"], input[name*="size"], select[name*="Size"], select[name*="size"]');
            const isSizeSelector = (legend && legend.textContent.toLowerCase().includes('size')) || sizeInputByName;

            if (isSizeSelector) {
              const radioInput = fieldset.querySelector('input[type="radio"]:checked');
              const selectInput = fieldset.querySelector('select');

              if (radioInput) {
                sizeInput = radioInput;
              } else if (selectInput && selectInput.value) {
                sizeInput = selectInput;
              }

              if (!sizeInput || !sizeInput.value) {
                const sizeErrorMessage = variantSelects.querySelector('.size-selection-error');

                if (sizeErrorMessage) {
                  sizeErrorMessage.style.display = 'block';
                  sizeErrorMessage.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }
                return;
              }
              break;
            }
          }
        }

        this.handleErrorMessage();

        this.submitButtons.forEach(button => {
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');

          // Hide the button text and show the loading spinner
          const buttonText = button.querySelector('span');
          const loadingSpinner = button.querySelector('.loading__spinner');

          if (buttonText) {
            buttonText.style.display = 'none';
          }

          if (loadingSpinner) {
            loadingSpinner.classList.remove('hidden');
          }
        });

        // Safety timeout to ensure buttons don't get permanently stuck
        this.safetyTimeout = setTimeout(() => {
          if (this.error !== true) { // Only reset if not a sold out error
            this.resetButtonStates();
          }
        }, 10000); // 10 second timeout

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // Clear safety timeout since we got a response
            if (this.safetyTimeout) {
              clearTimeout(this.safetyTimeout);
              this.safetyTimeout = null;
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              // Check if this is a sold out error or other type of error
              const isSoldOutError = response.description && response.description.toLowerCase().includes('sold out');

              if (isSoldOutError) {
                // Handle sold out state for all submit buttons
                this.submitButtons.forEach(button => {
                  const soldOutMessage = button.querySelector('.sold-out-message');
                  if (!soldOutMessage) return;
                  button.setAttribute('aria-disabled', true);
                  const buttonText = button.querySelector('span');
                  const loadingSpinner = button.querySelector('.loading__spinner');

                  if (buttonText) buttonText.classList.add('hidden');
                  if (loadingSpinner) loadingSpinner.classList.add('hidden');
                  soldOutMessage.classList.remove('hidden');
                });
                this.error = true;
              } else {
                // For other errors, just show the error message but keep buttons enabled
                this.error = false;
                // Reset button states to normal after non-sold-out errors
                this.resetButtonStates();
              }
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = false; // Reset error state on network errors

            // Clear safety timeout on error
            if (this.safetyTimeout) {
              clearTimeout(this.safetyTimeout);
              this.safetyTimeout = null;
            }
          })
          .finally(() => {
            // Clear safety timeout
            if (this.safetyTimeout) {
              clearTimeout(this.safetyTimeout);
              this.safetyTimeout = null;
            }

            // If it's not a sold out error, reset button states
            if (!this.error) {
              this.resetButtonStates();
            } else {
              // For sold out errors, just remove loading state but keep aria-disabled
              this.submitButtons.forEach(button => {
                button.classList.remove('loading');

                // Also restore button text and hide loading spinner
                const buttonText = button.querySelector('span');
                const loadingSpinner = button.querySelector('.loading__spinner');

                if (buttonText) {
                  buttonText.style.display = '';
                }

                if (loadingSpinner) {
                  loadingSpinner.classList.add('hidden');
                }
              });
            }

            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Restore button text and hide loading spinners
            this.submitButtons.forEach(button => {
              const buttonText = button.querySelector('span');
              const loadingSpinner = button.querySelector('.loading__spinner');

              if (buttonText) {
                buttonText.style.display = '';
              }

              if (loadingSpinner) {
                loadingSpinner.classList.add('hidden');
              }
            });

            CartPerformance.measureFromEvent("add:wait-for-subscribers", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        } else {
          // If clearing error message, also reset button states
          this.resetButtonStates();
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButtons.forEach((button, index) => {
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text && this.submitButtonTexts[index]) {
              this.submitButtonTexts[index].textContent = text;
            }
          } else {
            button.removeAttribute('disabled');
            if (this.submitButtonTexts[index]) {
              this.submitButtonTexts[index].textContent = window.variantStrings.addToCart;
            }
          }
        });
      }

      // Method to show/hide the appropriate button based on variant availability
      updateButtonVisibility(isAvailable) {
        // console.log(`updateButtonVisibility called with isAvailable: ${isAvailable}`);

        if (this.notifyMeButton) {
          this.notifyMeButton.style.display = isAvailable ? 'none' : 'block';
          // console.log(`Notify me button display set to: ${this.notifyMeButton.style.display}`);
        } else {
          // console.log('No notify me button found');
        }

        // Without a notify me button (product not tagged 'bis'), the disabled
        // sold out submit button must stay visible.
        const hideSubmit = !isAvailable && Boolean(this.notifyMeButton);
        this.submitButtons.forEach((button, index) => {
          button.style.display = hideSubmit ? 'none' : 'block';
          // console.log(`Submit button ${index} display set to: ${button.style.display}`);
        });

        // Refresh button references after HTML update
        this.refreshButtonReferences();
      }

      // Method to refresh button references after HTML updates
      refreshButtonReferences() {
        this.submitButtons = this.getSubmitButtons();
        this.submitButtonTexts = Array.from(this.submitButtons).map(button => button.querySelector('span'));
        this.notifyMeButton = this.querySelector('#NotifyMeButton-' + this.dataset.sectionId);

        // Re-add click event listener to notify me button
        if (this.notifyMeButton) {
          this.notifyMeButton.removeEventListener('click', this.openNotifyMeModal.bind(this));
          this.notifyMeButton.addEventListener('click', this.openNotifyMeModal.bind(this));
        }

        // console.log(`Refreshed button references: ${this.submitButtons.length} submit buttons, notify me button: ${!!this.notifyMeButton}`);
      }

      getSubmitButtons() {
        const submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));

        if (this.form?.id) {
          document.querySelectorAll(`[type="submit"][form="${this.form.id}"]`).forEach((button) => {
            if (!submitButtons.includes(button)) {
              submitButtons.push(button);
            }
          });
        }

        return submitButtons;
      }

      // Method to open the notify me modal
      openNotifyMeModal(event) {
        // console.log('Opening notify me modal');
        // Call the global function defined in main-product.liquid
        if (typeof window.openNotifyMeModal === 'function') {
          window.openNotifyMeModal(event);
        } else {
          console.warn('Global openNotifyMeModal function not found');
        }
      }

      // Method to reset button states after errors (except sold out)
      resetButtonStates() {
        this.submitButtons.forEach(button => {
          // Remove loading and aria-disabled states
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');

          // Reset button text to normal state
          const buttonText = button.querySelector('span');
          const soldOutMessage = button.querySelector('.sold-out-message');
          const loadingSpinner = button.querySelector('.loading__spinner');

          if (buttonText) {
            buttonText.style.display = '';
            if (soldOutMessage) {
              buttonText.classList.remove('hidden');
              soldOutMessage.classList.add('hidden');
            }
          }

          if (loadingSpinner) {
            loadingSpinner.classList.add('hidden');
          }

          // Re-enable the button
          button.removeAttribute('disabled');
        });
      }

      setupSizeSelectionErrorHandler() {
        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) return;

        variantSelects.addEventListener('change', (event) => {
          const target = event.target;
          const fieldset = target.closest('fieldset, .product-form__input--dropdown');

          if (fieldset) {
            const legend = fieldset.querySelector('legend, label');
            const sizeInputByName = fieldset.querySelector('input[name*="Size"], input[name*="size"], select[name*="Size"], select[name*="size"]');
            const isSizeSelector = (legend && legend.textContent.toLowerCase().includes('size')) || sizeInputByName;

            if (isSizeSelector) {
              const sizeErrorMessage = variantSelects.querySelector('.size-selection-error');
              if (sizeErrorMessage) {
                sizeErrorMessage.style.display = 'none';
              }
            }
          }
        });
      }

      preselectSmallestSizeOnDesktop() {
        if (window.innerWidth < 750) return;

        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) return;

        const sizeFieldsets = Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));

        for (const fieldset of sizeFieldsets) {
          const legend = fieldset.querySelector('legend, label');
          const sizeInputByName = fieldset.querySelector('input[name*="Size"], input[name*="size"], select[name*="Size"], select[name*="size"]');
          const isSizeSelector = (legend && legend.textContent.toLowerCase().includes('size')) || sizeInputByName;

          if (isSizeSelector) {
            const alreadyChecked = fieldset.querySelector('input[type="radio"]:checked');
            const selectElement = fieldset.querySelector('select');

            if (alreadyChecked) return;
            if (selectElement && selectElement.value) return;

            const firstRadio = fieldset.querySelector('input[type="radio"]:not([disabled]):not(.disabled)');
            if (firstRadio) {
              firstRadio.click();
              return;
            }

            if (selectElement) {
              const firstOption = selectElement.querySelector('option:not([disabled]):not(.disabled):not([value=""])');
              if (firstOption) {
                selectElement.value = firstOption.value;
                selectElement.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            break;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
